export const SELECT_OPERATORS = [
  { name: "isEqualTo", label: "is equal to" },
  { name: "isNotEqualTo", label: "is not equal to" },
  { name: "hasAnyOf", label: "has any of", isArray: true },
  { name: "hasNoneOf", label: "has none of", isArray: true },
  { name: "exists", label: "exists" },
  { name: "doesNotExist", label: "does not exist" },
];

// Segment filters are expanded to their stored filter groups by the backend.
// Without group negation, only positive membership can be represented safely.
export const SEGMENT_OPERATORS = [
  { name: "isEqualTo", label: "is" },
  { name: "hasAnyOf", label: "is any of", isArray: true },
];

// A history is membership only: there is no "exists" for stages moved through.
export const LIFECYCLE_HISTORY_OPERATORS = [
  { name: "hasAnyOf", label: "includes any of", isArray: true },
  { name: "hasNoneOf", label: "includes none of", isArray: true },
];

// Membership of a run in progress: the existence pair asks about any
// workflow, the list pair about the picked ones.
export const ACTIVE_WORKFLOW_OPERATORS = [
  { name: "exists", label: "is in any workflow" },
  { name: "doesNotExist", label: "isn't in any workflow" },
  { name: "hasAnyOf", label: "is in any of", isArray: true },
  { name: "hasNoneOf", label: "is in none of", isArray: true },
];

export const TIME_OPERATORS = [
  { name: "between", label: "between" },
  { name: "before", label: "before" },
  { name: "after", label: "after" },
  { name: "inTheLast", label: "in the last" },
  { name: "moreThanAgo", label: "more than ... ago" },
];

// Which broadcast. The window moved to its own `broadcastActivity` field, so
// `in the last` on this field no longer silently means "any broadcast", and the
// negatives are gone because the status word carries the "not".
export const BROADCAST_OPERATORS = [
  { name: "isEqualTo", label: "is" },
  { name: "hasAnyOf", label: "is any of", isArray: true },
  { name: "exists", label: "any broadcast" },
];

// What became of the send. One status per rule: the states nest (read implies
// delivered implies sent), so "any of" would only ever mean the widest one
// picked, and the timestamp the window reads would be ambiguous.
export const BROADCAST_STATUS_OPERATORS = [{ name: "isEqualTo", label: "is" }];

// When it happened to *this* contact. Absolute dates are deliberately absent -
// nobody asks "failed before 1 Sep" - and the negated window is gone with them,
// because "status is Not sent" already negates the whole match.
export const BROADCAST_ACTIVITY_OPERATORS = [
  { name: "inTheLast", label: "in the last" },
  { name: "moreThanAgo", label: "more than ... ago" },
];

/** Operators whose value is a relative window rather than a date. */
export const RELATIVE_TIME_OPERATORS = [
  "inTheLast",
  "moreThanAgo",
  "notInTheLast",
];

export const BOOLEAN_OPERATORS = [{ name: "isEqualTo", label: "is" }];

// Appointment ("booked a treatment") and order filters: existence operators
// answer who has / hasn't, the date operators narrow by when it happened. The
// operator names match the contact-filter enum the backend expects; only the
// labels differ so each reads naturally for its entity.
export const APPOINTMENT_OPERATORS = [
  { name: "exists", label: "has been booked" },
  { name: "doesNotExist", label: "hasn't been booked" },
  { name: "between", label: "booked between" },
  { name: "before", label: "booked before" },
  { name: "after", label: "booked after" },
  { name: "inTheLast", label: "booked in the last" },
  { name: "moreThanAgo", label: "booked more than ... ago" },
];

// Paid's value is the window; the amount rides on the rule as `atLeast`.
export const PAID_OPERATORS = [
  { name: "exists", label: "any time" },
  { name: "inTheLast", label: "in the last" },
  { name: "between", label: "between" },
  { name: "before", label: "before" },
  { name: "after", label: "after" },
];

export const VISIT_HISTORY_OPERATORS = [
  { name: "hasAnyOf", label: "is any of", isArray: true },
  { name: "hasNoneOf", label: "is none of", isArray: true },
];

export const ORDER_OPERATORS = [
  { name: "exists", label: "has been placed" },
  { name: "doesNotExist", label: "hasn't been placed" },
  { name: "between", label: "placed between" },
  { name: "before", label: "placed before" },
  { name: "after", label: "placed after" },
  { name: "inTheLast", label: "placed in the last" },
  { name: "moreThanAgo", label: "placed more than ... ago" },
];

export const TEXT_OPERATORS = [
  { name: "contains", label: "contains" },
  { name: "=", label: "equals" },
  { name: "beginsWith", label: "starts with" },
  { name: "doesNotBeginWith", label: "doesn't start with" },
  { name: "endsWith", label: "ends with" },
  { name: "null", label: "is empty" },
  { name: "notNull", label: "is not empty" },
];

// Mirrors the backend DYNAMIC_FIELD_OPERATORS whitelist exactly. A dynamic
// value is a string, a number or one of a fixed set, so the date and relation
// operators have nothing to act on - and anything off this list is rejected.
export const DYNAMIC_TEXT_OPERATORS = [
  { name: "contains", label: "contains" },
  { name: "isEqualTo", label: "is equal to" },
  { name: "isNotEqualTo", label: "is not equal to" },
  { name: "beginsWith", label: "starts with" },
  { name: "doesNotBeginWith", label: "doesn't start with" },
  { name: "endsWith", label: "ends with" },
  { name: "exists", label: "is not empty" },
  { name: "doesNotExist", label: "is empty" },
];

// Equality only. Range comparison would need a new branch in the backend's
// buildDynamicFieldCondition, which does not exist.
export const DYNAMIC_NUMBER_OPERATORS = [
  { name: "isEqualTo", label: "is equal to" },
  { name: "isNotEqualTo", label: "is not equal to" },
  { name: "exists", label: "is not empty" },
  { name: "doesNotExist", label: "is empty" },
];

export const DYNAMIC_SELECT_OPERATORS = [
  { name: "isEqualTo", label: "is equal to" },
  { name: "isNotEqualTo", label: "is not equal to" },
  { name: "hasAnyOf", label: "has any of", isArray: true },
  { name: "hasNoneOf", label: "has none of", isArray: true },
  { name: "exists", label: "is not empty" },
  { name: "doesNotExist", label: "is empty" },
];
